/**
 * No-reload colour switching on the PDP.
 *
 * Colours that live on one product are already instant - they are radio inputs
 * the theme resolves client side. Colours that live on a *separate* product are
 * anchors, so picking one used to cost a full page load. This intercepts those
 * anchors, fetches the sibling product page, and swaps the product section in
 * place, keeping scroll position.
 *
 * Only chips carrying data-variant-swap are intercepted. snippets/
 * variant-color-slides.liquid emits that attribute only when the product has a
 * custom.variant_products list; without it the chips stay ordinary links and
 * navigate with a reload, exactly as before.
 *
 * Why this seam: all three PDP sections (main-product, swatch-template,
 * swatch-size-template) render the same [data-section-type="main-product"]
 * wrapper with the same data-components, so the theme's own section contract
 * covers all of them and a sibling on a different template still swaps cleanly.
 * We replace the wrapper's contents rather than the wrapper itself so the
 * theme's registered section instance keeps pointing at a live node.
 *
 * Degrades to normal navigation whenever anything is missing or throws, so a
 * shopper is never stranded on a half-swapped page.
 */
(function () {
  'use strict';

  // The <script> tag for this file sits inside .product-detail, the node
  // quickbuy clones into its modal via jQuery .html(). jQuery's domManip
  // re-fetches and re-evaluates <script src> in inserted content, so opening a
  // quickbuy on a PDP would otherwise run a second copy of this IIFE with its
  // own cache and its own listeners: two fetches, two history entries and
  // apply() running twice per chip click.
  if (window.__pdpVariantSwapInstalled) return;
  window.__pdpVariantSwapInstalled = true;

  var SECTION_SELECTOR = '[data-section-type="main-product"]';
  var LINK_SELECTOR = 'a.pdp-variant-picker__slide--related[data-variant-swap]';
  var BUSY_CLASS = 'pdp-variant-swapping';
  var CACHE_LIMIT = 10;
  // Price and availability go stale, and a sold-out variant rendered as
  // buyable is worse than a second of latency, so entries expire by age as
  // well as by count.
  var CACHE_TTL = 90000;

  var cache = new Map();
  // Monotonic token rather than AbortController: the cached entry is a shared
  // promise, so aborting one caller's fetch would reject it for every other
  // caller holding the same entry. A superseded swap just drops its own result.
  var swapId = 0;

  function themeReady() {
    return !!(
      window.jQuery &&
      window.theme &&
      window.theme.Sections &&
      window.theme.initProductGallery
    );
  }

  /* One failing teardown step must not abandon the swap half-done. */
  function step(label, fn) {
    try {
      fn();
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[pdp-variant-swap] ' + label + ' failed', e);
      }
    }
  }

  /**
   * Anything observing a node inside the product section is watching markup
   * this file is about to detach. The Tabby promo block is the known case: it
   * observes the price element by id from inside a script.onload callback, so a
   * swap strands that observer on a detached price subtree and the re-run
   * creates another one.
   *
   * Wrapping the constructor once, at install time, catches those async
   * registrations - a shim held only for the duration of runInlineScripts would
   * miss them. Only observers whose target lives inside the section are
   * recorded, and they are disconnected when that section's contents go.
   */
  var observed = [];

  (function trackSectionObservers() {
    var Native = window.MutationObserver;
    if (!Native || Native.__pdpTracked) return;

    function Tracked(callback) {
      var instance = new Native(callback);
      var observe = instance.observe;
      instance.observe = function (target, options) {
        if (target && target.nodeType === 1 && target.closest && target.closest(SECTION_SELECTOR)) {
          observed.push({ observer: instance, target: target });
        }
        return observe.call(instance, target, options);
      };
      return instance;
    }

    Tracked.prototype = Native.prototype; // keeps instanceof working
    Tracked.__pdpTracked = true;
    window.MutationObserver = Tracked;
  })();

  function disconnectSectionObservers(section) {
    observed = observed.filter(function (entry) {
      if (!section.contains(entry.target)) return true;
      try {
        entry.observer.disconnect();
      } catch (e) {
        /* already gone */
      }
      return false;
    });
  }

  /**
   * Shopify's own section lifecycle events, dispatched on the .shopify-section
   * wrapper exactly as a theme-editor re-render does.
   *
   * This replaces a hand-rolled re-init that called onSectionLoad directly.
   * That was wrong twice over: it invoked the section hooks with the wrong
   * `this` and never updated theme.Sections' instance registry, and - because
   * it was private to this file - nothing outside the theme heard about the
   * swap, so every app that injects UI into the PDP (wishlist buttons, reviews
   * widgets, payment promos) kept pointing at markup that no longer existed.
   *
   * theme.js listens for these unconditionally and runs its full
   * sectionUnload/sectionLoad, which covers components, the gallery, the
   * product form and the scroll-reveal pass. Apps listen for the same events.
   * One dispatch, everything re-initialises the way it does on a real load.
   */
  function sectionHost(section) {
    return section.closest('.shopify-section');
  }

  function dispatchSectionEvent(host, name) {
    host.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: { sectionId: host.id.replace(/^shopify-section-/, '') }
      })
    );
  }

  /**
   * innerHTML never executes <script>. Product blocks can ship inline scripts
   * that depend on the markup around them - the Tabby promo block reads the
   * price element by id and then observes it - so those have to be re-run by
   * hand or they keep watching the detached node from the previous product.
   * Their observers are torn down by disconnectSectionObservers above.
   *
   * Scripts with src are skipped: theme assets are already on the page and
   * re-adding them would double-register their handlers.
   */
  function runInlineScripts(section) {
    var scripts = section.querySelectorAll('script:not([src])');
    Array.prototype.forEach.call(scripts, function (old) {
      var type = (old.getAttribute('type') || '').toLowerCase();
      // Leave data blocks alone - ProductJson and model-json are read, not run.
      if (type && type !== 'text/javascript' && type !== 'application/javascript') {
        return;
      }
      var fresh = document.createElement('script');
      fresh.textContent = old.textContent;
      if (old.parentNode) old.parentNode.replaceChild(fresh, old);
    });
  }

  /**
   * The size guide popup is rendered by the layout, outside the swapped
   * section, but its contents are product-specific: the caption carries
   * product.title and product.tags decide whether the mens, womens or juniors
   * table is shown. Left alone it keeps describing the product the shopper just
   * switched away from, which for a size chart is worse than merely stale.
   */
  function syncOutOfSectionContent(doc) {
    var current = document.getElementById('size-guide-popup');
    var incoming = doc.getElementById('size-guide-popup');
    if (current && incoming) current.innerHTML = incoming.innerHTML;
  }

  /**
   * Swym (wishlist) has no placeholder in the theme markup: swym-shopify.js
   * injects its PDP button itself, working from a snapshot of the product that
   * #swym-snippet writes into window.SwymPageData on load. After a swap that
   * snapshot still describes the previous product and the injected button went
   * out with the old markup. Re-running the new page's snippet is Swym's own
   * init path - it redefines swymPageLoad for this product and, with Swym
   * already loaded, calls initSwymShopify(), which re-injects the button.
   */
  function refreshWishlist(doc) {
    var current = document.getElementById('swym-snippet');
    var incoming = doc.getElementById('swym-snippet');
    if (!current || !incoming) return;
    var fresh = document.createElement('script');
    fresh.id = 'swym-snippet';
    fresh.textContent = incoming.textContent;
    current.parentNode.replaceChild(fresh, current);
  }

  /* Product JSON-LD is rendered outside the swapped wrapper, so sync it too. */
  function syncStructuredData(doc, section) {
    var host = section.closest('.shopify-section');
    var nextSection = doc.querySelector(SECTION_SELECTOR);
    var nextHost = nextSection && nextSection.closest('.shopify-section');
    if (!host || !nextHost) return;

    var current = host.querySelectorAll('script[type="application/ld+json"]');
    var incoming = nextHost.querySelectorAll('script[type="application/ld+json"]');
    if (!current.length || current.length !== incoming.length) return;

    for (var i = 0; i < current.length; i++) {
      current[i].textContent = incoming[i].textContent;
    }
  }

  function syncMeta(doc, selector) {
    var current = document.querySelector(selector);
    var incoming = doc.querySelector(selector);
    if (current && incoming) {
      current.setAttribute('content', incoming.getAttribute('content') || '');
    }
  }

  function syncHead(doc) {
    if (doc.title) document.title = doc.title;

    var canonical = document.querySelector('link[rel="canonical"]');
    var nextCanonical = doc.querySelector('link[rel="canonical"]');
    if (canonical && nextCanonical) {
      canonical.setAttribute('href', nextCanonical.getAttribute('href'));
    }

    syncMeta(doc, 'meta[name="description"]');

    ['og:url', 'og:title', 'og:image', 'og:description'].forEach(function (prop) {
      syncMeta(doc, 'meta[property="' + prop + '"]');
    });
  }

  /**
   * The section is rewritten silently, so a screen reader stays on the previous
   * product with no cue that anything moved. One polite live region carrying
   * the new title is the smallest thing that announces the change without
   * stealing focus from the colour strip the shopper is still working through.
   */
  function announce(section) {
    var region = document.getElementById('pdp-variant-swap-status');
    if (!region) {
      region = document.createElement('div');
      region.id = 'pdp-variant-swap-status';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    var title = section.querySelector('.product-title, h1');
    region.textContent = title ? title.textContent.trim() : document.title;
  }

  function remember(url, entry) {
    if (cache.size >= CACHE_LIMIT) {
      cache.delete(cache.keys().next().value);
    }
    cache.set(url, entry);
  }

  /**
   * The promise, not the resolved text, is what gets cached. mouseover refires
   * for every descendant of a chip and touchstart is followed by click, so
   * concurrent callers for one URL are the normal case rather than the edge
   * one - caching only on completion meant a full page download per event.
   */
  function fetchPage(url) {
    var hit = cache.get(url);
    if (hit && Date.now() - hit.at < CACHE_TTL) return hit.promise;

    var promise = fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    });

    promise.catch(function () {
      var current = cache.get(url);
      if (current && current.promise === promise) cache.delete(url);
    });

    remember(url, { promise: promise, at: Date.now() });
    return promise;
  }

  /**
   * Scroll-reveal animations are opt-in per element and driven by a single
   * IntersectionObserver that only ever scanned the original DOM. Injected
   * markup keeps data-cc-animate, so `.cc-animate-enabled [data-cc-animate]`
   * pins it at opacity:0 above 768px and it never receives the `-in` class that
   * would reveal it - the whole product section renders invisible on desktop.
   *
   * Stripping the attributes on the way in is the theme's own answer for
   * AJAX-replaced content (see the cart drawer in theme.js), and it is the right
   * one here too: this content is replacing something the shopper is already
   * looking at, so it should appear at once rather than re-animate.
   */
  function stripRevealAnimations(root) {
    root.querySelectorAll('[data-cc-animate]').forEach(function (el) {
      el.removeAttribute('data-cc-animate');
    });
    root.querySelectorAll('.fade-in').forEach(function (el) {
      el.classList.remove('fade-in');
    });
  }

  function apply(html, url, section) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector(SECTION_SELECTOR);
    if (!next) throw new Error('no product section in response');

    // With no host there is nothing to dispatch the lifecycle events on, so the
    // new markup would land with no gallery, no product form and no add-to-cart
    // wiring. Bail before touching the DOM and let the caller navigate instead
    // of handing the shopper a dead page.
    var host = sectionHost(section);
    if (!host) throw new Error('product section has no .shopify-section host');

    stripRevealAnimations(next);

    step('section unload', function () {
      dispatchSectionEvent(host, 'shopify:section:unload');
    });

    disconnectSectionObservers(section);

    section.innerHTML = next.innerHTML;
    runInlineScripts(section);

    step('section load', function () {
      dispatchSectionEvent(host, 'shopify:section:load');
    });

    // Cosmetic and SEO parity only, and they run after the markup the shopper
    // is looking at has already been replaced - a throw here must not trigger
    // the caller's fallback reload of a page that rendered fine.
    step('out-of-section content', function () {
      syncOutOfSectionContent(doc);
    });
    step('structured data', function () {
      syncStructuredData(doc, section);
    });
    step('head', function () {
      syncHead(doc);
    });
    step('announce', function () {
      announce(section);
    });
    step('wishlist', function () {
      refreshWishlist(doc);
    });

    section.dispatchEvent(
      new CustomEvent('pdp:variant-swapped', { bubbles: true, detail: { url: url } })
    );
  }

  function mainSection() {
    return document.querySelector(SECTION_SELECTOR);
  }

  function swap(url, push, section) {
    if (!section) {
      window.location.href = url;
      return;
    }

    var id = ++swapId;
    section.classList.add(BUSY_CLASS);

    fetchPage(url)
      .then(function (html) {
        if (id !== swapId) return; // superseded by a later click

        // Update the address bar BEFORE hydrating, not after. initProductOptions
        // reads location.search to apply option params, and the form rewrites
        // the URL on variant change, so hydrating first would initialise the
        // new product against the previous product's URL - applying its
        // ?variant= to a product it does not belong to. If apply() throws we
        // navigate to this same url anyway, so committing it early is safe.
        if (push) {
          // Mark the entry we are leaving too, so going back from a swap lands
          // in popstate here instead of showing the previous product's markup
          // under this product's URL.
          if (!history.state || !history.state.pdpVariantSwap) {
            history.replaceState({ pdpVariantSwap: true }, '', window.location.href);
          }
          history.pushState({ pdpVariantSwap: true }, '', url);
        }

        apply(html, url, section);
        section.classList.remove(BUSY_CLASS);
      })
      .catch(function (err) {
        if (id !== swapId) return;
        if (window.console && console.warn) {
          console.warn('[pdp-variant-swap] falling back to navigation', err);
        }
        window.location.href = url;
      });
  }

  function linkFrom(target) {
    return target && target.closest ? target.closest(LINK_SELECTOR) : null;
  }

  /**
   * Only chips belonging to the section this file swaps are ours. A quickbuy
   * modal opened from the recommendations carousel renders its own colour
   * chips; intercepting one of those would replace the page behind the modal
   * and rewrite the address bar to a product the shopper never opened.
   * Returning null leaves them as plain links.
   */
  function swappableSection(link) {
    var owner = link.closest(SECTION_SELECTOR);
    return owner && owner === mainSection() ? owner : null;
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    // Let the browser handle open-in-new-tab / download modifiers.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = linkFrom(e.target);
    if (!link) return;
    if (link.hostname !== window.location.hostname) return;
    if (!themeReady()) return; // no theme yet - let it navigate normally

    var section = swappableSection(link);
    if (!section) return; // quickbuy modal or detached markup - navigate normally

    e.preventDefault();
    swap(link.href, true, section);
  });

  /* Warm the cache on intent so the swap itself is usually instant. */
  function prefetch(e) {
    var link = linkFrom(e.target);
    if (!link || link.hostname !== window.location.hostname) return;
    // Nothing here could ever be swapped, so the download would be wasted.
    if (!themeReady() || !swappableSection(link)) return;
    fetchPage(link.href).catch(function () {
      /* prefetch is best effort */
    });
  }

  document.addEventListener('mouseover', prefetch);
  document.addEventListener('touchstart', prefetch, { passive: true });

  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.pdpVariantSwap) return;
    if (!themeReady()) {
      window.location.reload();
      return;
    }
    swap(window.location.href, false, mainSection());
  });
})();
